import {AuthForm, AuthHeading, AuthSubTitle, AuthTitle, AuthWrapper} from '@/components/ui/auth-atoms/AuthAtoms'
import i18n, {useTranslation} from '@/translations/i18n'
import {useForm, useWatch} from 'react-hook-form'
import {zodResolver} from '@hookform/resolvers/zod'
import {z} from 'zod'
import {Button} from '@/components/ui/button/Button'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {InputText} from '@/components/commons/input-text/InputText'
import {useTheme} from 'styled-components'
import {AlertCircleIcon} from '@/components/ui/icon/Icon'
import {ButtonLink} from '@/components/ui/button-link/ButtonLink'
import {routes} from '@/utilities/constants/routes'
import {useParsedSearchParams} from '@/hooks/useParsedSearchParams'
import {useNavigate} from 'react-router-dom'
import {useCallback, useEffect, useState} from 'react'
import {useVerifyRecoveryToken} from '@/features/auth/services/useVerifyRecoveryToken'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {useResetPassword} from '@/features/auth/services/useResetPassword'
import {errorHandler} from '@/utilities/helpers'
import {Helmet} from 'react-helmet'

const ResetPasswordSearchParams = z.object({token: z.string().trim()})
type ResetPasswordSearchParams = z.infer<typeof ResetPasswordSearchParams>

const ResetPasswordValidationSchema = z
    .object({
        password: z.string().min(8, i18n.t('commons:password_rules')),
        confirmPassword: z.string()
    })
    .superRefine(({password, confirmPassword}, ctx) => {
        if (confirmPassword != password) {
            ctx.addIssue({
                code: 'custom',
                message: i18n.t('errors:confirm_password'),
                path: ['confirmPassword']
            })
        }
    })
type ResetPasswordValidationSchema = z.infer<typeof ResetPasswordValidationSchema>

export const ResetPassword = () => {
    const {t} = useTranslation()
    const [verifiedToken, setVerifiedToken] = useState<boolean | null>(null)
    const {searchParams} = useParsedSearchParams(ResetPasswordSearchParams)
    const {palette} = useTheme()
    const navigate = useNavigate()

    const form = useForm<ResetPasswordValidationSchema>({
        mode: 'onBlur',
        resolver: zodResolver(ResetPasswordValidationSchema)
    })

    // mutations
    const verifyRecoveryToken = useVerifyRecoveryToken({
        onSuccess: () => setVerifiedToken(true),
        onError: () => setVerifiedToken(false)
    })
    const resetPassword = useResetPassword({
        onSuccess: () => navigate(routes.LOGIN.path, {replace: true}),
        onError: error => errorHandler(error)
    })

    // form values
    const password = useWatch({control: form.control, name: 'password'})
    const confirmPassword = useWatch({control: form.control, name: 'confirmPassword'})

    // verify token on first access
    const runTokenVerification = useCallback((token: string) => {
        verifyRecoveryToken.mutate({token})
    }, [])

    useEffect(() => {
        runTokenVerification(searchParams.token)
    }, [searchParams.token])

    // form submission
    const handleSubmit = form.handleSubmit(() =>
        resetPassword.mutate({
            password,
            passwordConfirmation: confirmPassword,
            token: searchParams.token
        })
    )

    return (
        <AuthWrapper>
            <Helmet title={t('reset_password:meta_title')} />
            {verifiedToken == null || verifyRecoveryToken.isPending ? (
                <Spinner size={40} />
            ) : (
                <AuthForm fullWidth align="center" justify="center" gap={8} onSubmit={handleSubmit}>
                    {verifiedToken == false ? (
                        <Flexbox fullWidth direction="column" gap={6} align="center">
                            <AlertCircleIcon size={32} color={palette.danger[500]} />
                            <Flexbox direction="column" gap={2} align="center">
                                <AuthTitle>{t('commons:invalid_link')}</AuthTitle>
                                <AuthSubTitle>{t('reset_password:subtitle_error')}</AuthSubTitle>
                            </Flexbox>
                            <ButtonLink to={routes.FORGOT_PASSWORD.path} variant="secondary" fullWidth>
                                {t('commons:forgot_password')}
                            </ButtonLink>
                        </Flexbox>
                    ) : (
                        <>
                            <AuthHeading
                                showLogo
                                title={t('reset_password:title')}
                                subtitle={t('reset_password:subtitle')}
                            />
                            <Flexbox direction="column" gap={4} fullWidth>
                                <InputText
                                    type="password"
                                    label={t('commons:password')}
                                    placeholder={'••••••••••'}
                                    errorMessage={form.formState.errors.password?.message}
                                    helpText={t('commons:password_rules')}
                                    visibilityToggle
                                    {...form.register('password')}
                                />
                                <InputText
                                    type="password"
                                    label={t('commons:repeat_password')}
                                    placeholder={'••••••••••'}
                                    visibilityToggle
                                    touched={form.formState.isDirty}
                                    errorMessage={form.formState.errors.confirmPassword?.message}
                                    successMessage={
                                        form.formState.isValid && password == confirmPassword
                                            ? t('commons:password_match')
                                            : ''
                                    }
                                    {...form.register('confirmPassword')}
                                />
                            </Flexbox>
                            <Button type="submit" fullWidth variant="primaryNeutral">
                                {t('reset_password:cta')}
                            </Button>
                        </>
                    )}
                </AuthForm>
            )}
        </AuthWrapper>
    )
}

ResetPassword.displayName = 'ResetPassword'
